import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Mountain, DollarSign, FolderKanban, TrendingUp } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer, cardVariants } from '../utils/motion';
import { useInViewOnce } from '../hooks/useInViewOnce';

const serviceIcons = {
  technology: Cpu,
  mining: Mountain,
  financial: DollarSign,
  'project-management': FolderKanban,
  'business-advisory': TrendingUp
};

export const Services = () => {
  const [ref, isInView] = useInViewOnce();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              variants={revealVariants}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <span>Our Services</span>
            </motion.div>

            <motion.h1 
              variants={revealVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Multi-Sector <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Expertise</span>
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Comprehensive consulting solutions across Technology, Mining, Finance, Project Management, 
              and Business Advisory services. Each service is designed to deliver measurable results 
              and drive sustainable growth.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            ref={ref}
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 gap-12"
          >
            {mockData.services.map((service, index) => {
              const IconComponent = serviceIcons[service.id];
              return (
                <motion.div
                  key={service.id}
                  variants={revealVariants}
                  className="group"
                >
                  <motion.div
                    variants={cardVariants}
                    whileHover="hover"
                    className="bg-gradient-to-br from-white to-slate-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 relative overflow-hidden h-full"
                  >
                    {/* Background Gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FF5C8A]/10 to-[#FF8C42]/10 rounded-full transform translate-x-16 -translate-y-16" />
                    
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <IconComponent size={32} className="text-white" />
                        </div>
                        <ArrowRight size={24} className="text-gray-400 group-hover:text-[#FF5C8A] transition-colors" />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#1F3C88] transition-colors">
                        {service.name}
                      </h3>
                      
                      <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                        {service.description}
                      </p>
                      
                      <div className="space-y-3 mb-8">
                        {service.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full" />
                            <span className="text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>

                      {/* KPIs */}
                      <div className="grid grid-cols-3 gap-4 mb-8">
                        {service.kpis.map((kpi, idx) => (
                          <div key={idx} className="text-center bg-white/80 rounded-xl p-3 border border-gray-100">
                            <div className="text-2xl font-bold text-[#1F3C88]">{kpi.value}</div>
                            <div className="text-xs text-gray-600">{kpi.label}</div>
                          </div>
                        ))}
                      </div>
                      
                      <Link
                        to={`/services/${service.id}`}
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow group"
                      >
                        <span>Learn More</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1F3C88] to-[#2d4ba3]">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-white mb-6"
            >
              Ready to discuss your project?
            </motion.h2>
            
            <motion.p 
              variants={revealVariants}
              className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto"
            >
              Let's explore how our multi-sector expertise can help achieve your specific goals.
            </motion.p>
            
            <motion.div variants={revealVariants}>
              <Link
                to="/request-service"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-shadow"
              >
                <span>Request Service</span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};